import { useToasts } from '@brunommsilva/react-foundations';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useUpdateTransaction from '../../../hooks/transactions/use-update-transaction';
import { Transaction } from '../../../models/transactions';
import TransactionForm, { FormState } from './transaction-form';

interface EditTransactionFormProps {
  transaction: Transaction;
  onSuccess?: () => void;
  onCancel?: () => void;
  isDialog?: boolean;
}

const EditTransactionForm = ({
  transaction,
  onSuccess,
  onCancel,
  isDialog = false,
}: EditTransactionFormProps) => {
  const { t } = useTranslation();
  const { showToast } = useToasts();
  const {
    updateTransaction,
    isUpdating,
    wasUpdatedWithSuccess,
    wasUpdatedWithoutSuccess,
  } = useUpdateTransaction();

  useEffect(() => {
    if (wasUpdatedWithSuccess) {
      showToast({
        title: t('EDIT_TRANSACTION_SUCCESS_TOAST.TITLE'),
        message: t('EDIT_TRANSACTION_SUCCESS_TOAST.MESSAGE'),
      });
      if (onSuccess) {
        onSuccess();
      }
    }
    if (wasUpdatedWithoutSuccess) {
      showToast({
        title: t('EDIT_TRANSACTION_ERROR_TOAST.TITLE'),
        message: t('EDIT_TRANSACTION_ERROR_TOAST.MESSAGE'),
        type: 'error',
      });
    }
  }, [
    wasUpdatedWithSuccess,
    wasUpdatedWithoutSuccess,
    onSuccess,
    showToast,
    t,
  ]);

  const getInitialValues = (): FormState => {
    return {
      date: transaction.date ? new Date(transaction.date) : undefined,
      type: transaction.type,
      otherAccount: transaction.account || undefined, // Maps to accountId in DB
      account: transaction.otherAccount || undefined, // Maps to otherAccountId in DB
      category: transaction.category || undefined,
      amount: transaction.amount,
      description: transaction.description,
    };
  };

  const handleSubmit = (values: FormState) => {
    updateTransaction({
      id: transaction.id,
      amount: values.amount!,
      date: values.date!,
      type: values.type!,
      description: values.description,
      otherAccountId: values.account?.id,
      accountId: values.otherAccount?.id || '',
      categoryId: values.category?.id || '',
    });
  };

  return (
    <TransactionForm
      initialValues={getInitialValues()}
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isSubmitting={isUpdating}
      submitLabel={t('EDIT_TRANSACTION_MODAL.CONFIRM')}
      isDialog={isDialog}
    />
  );
};

export default EditTransactionForm;
